import * as React from 'react';
import { Check, ChevronDown, Phone } from 'lucide-react';
import {
  getCountries,
  getCountryCallingCode,
  parsePhoneNumber,
  type CountryCode,
} from 'libphonenumber-js';
import * as CountryFlags from 'country-flag-icons/react/3x2';
import { cn } from '@/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';

const DEFAULT_COUNTRY: CountryCode = 'IN';

const regionNames =
  typeof Intl !== 'undefined' && 'DisplayNames' in Intl
    ? new Intl.DisplayNames(['en'], { type: 'region' })
    : null;

const FLAG_COMPONENTS = CountryFlags as unknown as Record<
  string,
  React.ComponentType<{ className?: string; title?: string }>
>;

function FlagIcon({ country, className }: { country: string; className?: string }) {
  const Flag = FLAG_COMPONENTS[country];
  if (!Flag) {
    return (
      <span
        className={cn(
          'inline-flex items-center justify-center rounded-[2px] bg-muted text-[8px] font-semibold text-muted-foreground',
          className,
        )}
      >
        {country}
      </span>
    );
  }
  return <Flag title={country} className={cn('rounded-[2px] object-cover', className)} />;
}

interface CountryOption {
  iso: CountryCode;
  name: string;
  dial: string;
}

const COUNTRIES: CountryOption[] = getCountries()
  .map((iso) => ({
    iso,
    name: regionNames?.of(iso) ?? iso,
    dial: getCountryCallingCode(iso),
  }))
  .sort((a, b) => a.name.localeCompare(b.name));

export interface PhoneNumberInputProps {
  /** E.164 string (e.g. "+919876543210"), or "" when empty. */
  value: string;
  /** Called with the E.164 string, or "" when the number field is empty. */
  onChange: (e164: string) => void;
  id?: string;
  disabled?: boolean;
  className?: string;
  error?: boolean;
  /** Country preselected when `value` is empty. Defaults to India. */
  defaultCountry?: CountryCode;
  placeholder?: string;
  onBlur?: () => void;
  autoFocus?: boolean;
  /** Test id placed on the number <input>. */
  'data-testid'?: string;
  /**
   * The field is a single joined box (flag + code | number). `size` sets its height and font:
   * "compact" (default) is h-8/sm:h-10 with small text; "default" matches the standard `Input`
   * (h-10, text-base / md:text-sm); "custom" applies no height or font at all, so `className`
   * (height, radius, border colour on the whole box), `inputClassName` and `triggerClassName`
   * (fonts) fully control them.
   */
  size?: 'compact' | 'default' | 'custom';
  /** Show the leading phone glyph inside the number field. Defaults to true. */
  showIcon?: boolean;
  /** Extra classes for the number <input> (typically just font / colour). */
  inputClassName?: string;
  /** Extra classes for the country trigger button (typically just font). */
  triggerClassName?: string;
}

export function PhoneNumberInput({
  value,
  onChange,
  id,
  disabled,
  className,
  error,
  defaultCountry = DEFAULT_COUNTRY,
  placeholder,
  onBlur,
  autoFocus,
  'data-testid': dataTestId,
  size = 'compact',
  showIcon = true,
  inputClassName,
  triggerClassName,
}: PhoneNumberInputProps) {
  const [open, setOpen] = React.useState(false);
  const [country, setCountry] = React.useState<CountryCode>(defaultCountry);
  const [national, setNational] = React.useState('');

  React.useEffect(() => {
    const v = (value ?? '').trim();
    if (!v) {
      setNational('');
      return;
    }
    try {
      const parsed = parsePhoneNumber(v, v.startsWith('+') ? undefined : defaultCountry);
      if (parsed?.country) setCountry(parsed.country);
      if (parsed?.nationalNumber) {
        setNational(String(parsed.nationalNumber));
        return;
      }
    } catch {
    }
    let fallbackDigits = v.replace(/\D/g, '');
    if (v.startsWith('+')) {
      const cc = getCountryCallingCode(country);
      if (fallbackDigits.startsWith(cc)) {
        fallbackDigits = fallbackDigits.slice(cc.length);
      }
    }
    setNational(fallbackDigits);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const emit = (nextCountry: CountryCode, nextNational: string) => {
    const digits = nextNational.replace(/\D/g, '');
    onChange(digits ? `+${getCountryCallingCode(nextCountry)}${digits}` : '');
  };

  const handleCountry = (iso: CountryCode) => {
    setCountry(iso);
    setOpen(false);
    emit(iso, national);
  };

  const handleNational = (raw: string) => {
    let digits = raw.replace(/\D/g, '');
    if (digits) {
      try {
        const parsed = parsePhoneNumber(raw.startsWith('+') ? raw : digits, country);
        if (
          parsed &&
          parsed.isValid() &&
          String(parsed.nationalNumber).length < digits.length
        ) {
          digits = String(parsed.nationalNumber);
        }
      } catch {
      }
    }
    setNational(digits);
    emit(country, digits);
  };

  const fontCls =
    size === 'compact' ? 'text-xs sm:text-sm' : size === 'default' ? 'text-base md:text-sm' : '';

  return (
    // One joined field: [flag +code ⌄ | number], like a single input rather than two separate boxes.
    <div
      className={cn(
        'flex w-full items-center rounded-md border border-input bg-background transition-colors hover:bg-slate-50',
        'focus-within:border-input-focus focus-within:ring-1 focus-within:ring-input-focus',
        size === 'compact' && 'h-8 sm:h-10',
        size === 'default' && 'h-10',
        error && 'border-destructive focus-within:border-destructive focus-within:ring-destructive',
        disabled && 'cursor-not-allowed opacity-50 hover:bg-background',
        className,
      )}
    >
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            role="combobox"
            aria-expanded={open}
            aria-label="Select country code"
            disabled={disabled}
            className={cn(
              'flex h-full shrink-0 items-center gap-1 rounded-l-[inherit] pl-2.5 pr-2 text-foreground outline-none transition-colors hover:bg-[#F9FAFB] focus-visible:bg-[#F9FAFB] disabled:cursor-not-allowed',
              fontCls,
              triggerClassName,
            )}
          >
            <FlagIcon country={country} className="h-3.5 w-5 shrink-0" />
            <span className="font-medium tabular-nums">+{getCountryCallingCode(country)}</span>
            <ChevronDown className="h-3 w-3 shrink-0 text-[#98A2B3]" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[270px] rounded-xl border border-[#E4E7EC] bg-white p-0 shadow-[0_8px_20px_rgba(16,24,40,0.08)]"
          align="start"
          side="bottom"
          sideOffset={4}
          collisionPadding={12}
        >
          <Command
            className="rounded-xl bg-white [&_[cmdk-input-wrapper]]:border-[#E4E7EC]"
            filter={(value, search) =>
              value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0
            }
          >
            <CommandInput
              placeholder="Search country…"
              className="h-10 text-[13px] font-medium text-[#101828] placeholder:text-[#98A2B3]"
            />
            <CommandList className="max-h-[360px] p-1">
              <CommandEmpty className="py-4 text-center text-[13px] text-[#667085]">
                No country found.
              </CommandEmpty>
              <CommandGroup className="p-0">
                {COUNTRIES.map((c) => (
                  <CommandItem
                    key={c.iso}
                    value={`${c.name} +${c.dial} ${c.iso}`}
                    onSelect={() => handleCountry(c.iso)}
                    className="cursor-pointer rounded-lg px-2 py-1.5 text-[13px] font-medium text-[#101828] data-[selected='true']:bg-[#F9FAFB] data-[selected='true']:text-[#101828] data-[selected=true]:bg-[#F9FAFB] data-[selected=true]:text-[#101828]"
                  >
                    <FlagIcon country={c.iso} className="mr-2 h-3.5 w-5 shrink-0" />
                    <span className="flex-1 truncate">{c.name}</span>
                    <span className="ml-2 text-[12px] font-normal text-[#667085]">+{c.dial}</span>
                    <Check
                      className={cn(
                        'ml-2 h-3.5 w-3.5 shrink-0 text-[#6B7280]',
                        country === c.iso ? 'opacity-100' : 'opacity-0',
                      )}
                    />
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <span className="h-5 w-px shrink-0 bg-[#E4E7EC]" aria-hidden="true" />
      <div className="relative h-full min-w-0 flex-1">
        {showIcon && (
          <Phone className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        )}
        <input
          id={id}
          data-testid={dataTestId}
          type="tel"
          inputMode="tel"
          autoComplete="tel-national"
          autoFocus={autoFocus}
          disabled={disabled}
          placeholder={placeholder}
          value={national}
          onChange={(e) => handleNational(e.target.value)}
          onBlur={onBlur}
          className={cn(
            'h-full w-full min-w-0 rounded-r-[inherit] bg-transparent px-2.5 tabular-nums text-foreground outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed',
            showIcon && 'pl-8',
            fontCls,
            inputClassName,
          )}
        />
      </div>
    </div>
  );
}
